import React, { Component } from 'react';
import './account-activity.scss';
import Select from '../../Form/Select/Select';
import ButtonGroup from '../../Form/Button/ButtonGroup';
import Button from '../../Form/Button/Button';
import calendarIcon from '../../../assets/images/calendar.svg';
import chevDown from '../../../assets/images/chev-down-royal.svg';
import withdrawIcon from '../../../assets/images/withdraw.svg';
import infoIcon from '../../../assets/images/info-icon.svg';
import interestEarnedIcon from '../../../assets/images/interest-earned.svg';
import successImg from '../../../assets/images/success.png';
import errorImg from '../../../assets/images/error.png';
import { formatDate, formatCurrency, joinClasses } from '../../../utils/helpers';
import withFeatureFlags from "../../FeatureFlags/withFeatureFlags";
import Modal from '../../Modal/Modal';
import Loading from '../../Loading/Loading';
import { gql } from 'apollo-boost';
import { useApolloClient } from '@apollo/react-hooks';
import {productTypes} from "../../../utils/productHelpers";

/** @import {ValueType} from 'react-select' */
/** @import { OptionType } from "../../Form/Select/SelectTypes" */
/** @import {ApolloClient} from 'apollo-client' */
/** @import {Account, PendingTransaction, Transaction} from '../DashboardTypes' */
/** @import {AccountActivityProps, AccountActivityState, FilterOption} from './AccountActivityTypes' */
/** @import { WithFeatureFlagsProps } from '../../FeatureFlags/FeatureFlagsTypes' */

const filterOptions = [
    { label: 'All', value: 'all' },
    { label: 'Interest Earned', value: 'interestEarned' },
    { label: 'Incoming', value: 'incoming' },
    { label: 'Outgoing', value: 'outgoing' }
];

const filterOptionsV2 = [
    { label: 'All', value: 'all' },
    { label: 'Pending withdrawals', value: 'pending' },
    { label: 'Interest Earned', value: 'interestEarned' },
    { label: 'Incoming', value: 'incoming' },
    { label: 'Outgoing', value: 'outgoing' }
];

const filterOptionsWithFees = [
    { label: 'All', value: 'all' },
    { label: 'Interest Earned', value: 'interestEarned' },
    { label: 'Incoming', value: 'incoming' },
    { label: 'Outgoing', value: 'outgoing' },
    { label: 'Fees', value: 'fees' }
];

const getFilterOptions = (/** @type {Account} */ account) => {
    // Check if it's a FISA account and has transactions with EarlyClosureFees or EarlyClosureFeeReversal
    const hasFees = account && account.transactions && account.transactions.some(t => t.action === 'EarlyClosureFees' || t.action === 'EarlyClosureFeeReversal');
    const isFISA = account && account.product && account.product.productType === productTypes.FixedRateCashISA;
    
    if (account && account.product && account.product.productType === productTypes.Notice
                    && account.pendingTransactions && account.pendingTransactions.length !== 0) {
        return filterOptionsV2;
    }
    
    if (isFISA && hasFees) {
        return filterOptionsWithFees;
    }
    
    return filterOptions;
}

/**
 * @template TElement
 * @param {TElement[]} array
 * @param {(item: TElement) => string} cb
 * @returns {Array<{ key: string, items: TElement[] }>}
 */
const groupBy = (array, cb) => {
  /** @type {Array<{ key: string, items: TElement[] }>} */
  let result = [];
  let lastKey = '';
  /** @type {null | { key: string, items: TElement[] }} */
  let lastResult = null;
  array.forEach(item => {
    let key = cb(item);
    if (key !== lastKey) {
      lastResult = { key, items: [item] };
      lastKey = key;
      result.push(lastResult);
    } else {
      if (lastResult) {
        lastResult.items.push(item);
      }
    }
  });
  return result;
};

/** @extends {Component<AccountActivityProps & WithFeatureFlagsProps & {client: ApolloClient<any>}, AccountActivityState>} */
class AccountActivity extends Component {
  /** @type {ApolloClient<any>} */
  apolloClient;

  /** @type {Record<string, () => void>} */
  selectFilters = {};

  /** @type {AccountActivityState} */
  state = {
    filter: getFilterOptions(this.props.account)[0],
    toggledActivities: [],
    withdrawalDateToolTipOpen: false,
    withdrawalDateToolTipLeft: 0,
    withdrawalDateToolTipTop: 0,
    cancelNoticeModalOpen: false,
    cancelNoticeSuccessModalOpen: false,
    cancelNoticeFailedModalOpen: false,
    pendingWithdrawalRecord: null,
    isProcessing:false
  };

  /** @type {React.RefObject<HTMLDivElement>} */
  popupWithdrawalDateTooltip = React.createRef();

  constructor(/** @type {AccountActivityProps & WithFeatureFlagsProps & {client: ApolloClient<any>}} */ props) {
      super(props);
      this.apolloClient = props.client;

      getFilterOptions(this.props.account).forEach(filter => {
        this.selectFilters[`selectFilter_${filter.value}`] = () => this.selectFilter(filter);
      });
  }

  componentDidMount() {
    document.body.addEventListener('click', this.onClickOutside, false);
  };

  componentWillUnmount() {
    document.body.removeEventListener('click', this.onClickOutside);
  };

  onClickOutside = (/** @type {MouseEvent} */ event) => {
        if (
            this.state.withdrawalDateToolTipOpen
            && this.popupWithdrawalDateTooltip.current
            && getComputedStyle(this.popupWithdrawalDateTooltip.current).display !== 'none'
            && event.target instanceof Element
            && !this.popupWithdrawalDateTooltip.current.contains(event.target)
        ) {
            event.preventDefault();
            event.stopPropagation();
            this.setState({ withdrawalDateToolTipOpen: false });
        }
  };

  selectFilter = (/** @type {ValueType<OptionType, false>} */ filter) => this.setState({ filter });

  // setStartDate = startDate => this.setState({ startDate });

  // setEndDate = endDate => this.setState({ endDate });

  toggleActivity = (/** @type {string} */ activityId) => (/** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ event) => {
    event.preventDefault();

    this.setState(({ toggledActivities }) => ({
        toggledActivities: toggledActivities.includes(activityId)
            ? toggledActivities.filter(id => id !== activityId)
            : [...toggledActivities, activityId]
    }));
  }

  filteredActivity() {
    const { account } = this.props;
    const { filter } = this.state;
    let activity = account.transactions;

    switch (filter && filter.value) {
      case 'interestEarned': return activity.filter(item => item.action === 'InterestEarned');
      case 'incoming': return activity.filter(item => item.amount > 0 && item.action === 'Deposit');
      case 'outgoing': return activity.filter(item => item.amount < 0 && item.action !== 'EarlyClosureFees' && item.action !== 'EarlyClosureFeeReversal');
      case 'fees': return activity.filter(item => item.action === 'EarlyClosureFees' || item.action === 'EarlyClosureFeeReversal');
      default: return activity;
    }
  }

  getClassName() {
    return joinClasses(
      "account-activity",
      this.props.disable ? "account-activity--disable" : ""
    );
  }

  renderActivityMetadata(/** @type {Transaction} */ activity, /** @type {FilterOption[]} */ activityMetadata) {
    const { toggledActivities } = this.state;
    let metadataOpen = toggledActivities.includes(activity.transactionId);
    return (
        <tr key={`${activity.transactionId}-metadata`} className={['account-activity__table-row account-activity__table-row--metadata', metadataOpen ? 'is-open' : ''].join(' ')}>
        <td colSpan={3} className="account-activity__table-cell account-activity__table-cell--metadata">
          <table className="account-activity__metadata-table">
            <tbody>
              {(activityMetadata || []).map(({ label, value }) => (
                <tr key={label} className="account-activity__metadata-table-row">
                  <td className="account-activity__metadata-table-cell account-activity__metadata-table-cell--key">{label}</td>
                  <td className="account-activity__metadata-table-cell account-activity__metadata-table-cell--value">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {activity.action === 'InterestEarned' && (
            <div className="account-activity__metadata-line">
              <strong>From</strong> {activity && activity.interestPeriodFrom ? formatDate(new Date(activity.interestPeriodFrom)) : ''} <strong>to</strong> {activity && activity.interestPeriodTo ? formatDate(new Date(activity.interestPeriodTo)) : ''}
            </div>
          )}
        </td>
      </tr>
    )
  }

  /** @returns {FilterOption[]} */
  buildActivityMetadata(/** @type {Transaction} */ activity) {
  if (activity.action === 'InterestEarned') {
    return [];
  }

  if (activity.action === 'CompensatingWithdrawal') {
    return [
      { label: "Reference", value: activity.reference },
    ]
  }

  if (activity.action === 'EarlyClosureFees' || activity.action === 'EarlyClosureFeeReversal') {
    // For penalty transactions, do not show any metadata
    return [];
  }

    if (activity.amount < 0) {
      return [
        { label: "Sent to:", value: " " },
        { label: "Account number", value: activity.counterpartAccountNumber },
        { label: "Sort code", value: activity.counterpartSortCode },
      ]
    }

    return [
      { label: "From:", value: " " },
      { label: "Account number", value: activity.counterpartAccountNumber },
      { label: "Sort code", value: activity.counterpartSortCode },
      { label: "Reference", value: activity.reference },
    ];
  }

  renderActivityLabel(/** @type {Transaction} */ activity) {
    switch(activity.action) {
      case "InterestEarned":
        return "Interest earned";
      case "InterestAdjustment":
        return this.props.featureEnabled('EnableMambuValueDate')
          ? "Interest adjustment"
          : "Money in";
      case "CompensatingWithdrawal":
        return "Money out";
      case "Withdraw":
        return "Money out";
      case "EarlyClosureFees":
        return "Early closure fee";
      case "EarlyClosureFeeReversal":
        return "Early closure fee reversal";
      default:
        return "Money in";
    }
  }

  renderActivity(/** @type {Transaction} */ activity) {
    const { toggledActivities } = this.state;
    let metadataOpen = toggledActivities.includes(activity.transactionId);

    const activityMetadata = this.buildActivityMetadata(activity);
    const hasMetadata = activityMetadata && activityMetadata.length > 0;
    const isEarlyClosure = activity.action === 'EarlyClosureFees' || activity.action === 'EarlyClosureFeeReversal';

    let mainRow = (
      <tr key={activity.transactionId} className="account-activity__table-row">
        <td className="account-activity__table-cell">
          {hasMetadata ? (
            <div className={['account-activity__activity-action', 'account-activity__activity-action--has-metadata', metadataOpen ? 'is-open' : ''].join(' ')}>
              <a href="#" className="account-activity__activity-action-toggle" onClick={this.toggleActivity(activity.transactionId)}>
                <img className="account-activity__activity-action-icon" src={activity.action === 'InterestEarned' ? interestEarnedIcon : withdrawIcon} />
                <img className="account-activity__activity-action-chevron" src={chevDown} />
              </a>
              <span className="account-activity__activity-action-label">{this.renderActivityLabel(activity)}</span>
            </div>
          ) : (
            <span className={['account-activity__activity-action-label', 'account-activity__activity-action-label--no-metadata'].join(' ')}>{this.renderActivityLabel(activity)}</span>
          )}
        </td>
        <td data-label="Amount" className={['account-activity__table-cell', activity.amount > 0 ? 'account-activity__incoming-amount' : ''].join(' ')}>
          {formatCurrency(activity.amount)}
        </td>
        <td data-label="Balance" className="account-activity__table-cell">{formatCurrency(activity.updatedBalance)}</td>
      </tr>
    );
    let result = [mainRow];
    if (hasMetadata) {
      result.push(this.renderActivityMetadata(activity, activityMetadata));
    }
    return result;
  }

  shouldShowFilter() {
      const { account } = this.props;
      const hasFees = account && account.transactions && account.transactions.some(t => t.action === 'EarlyClosureFees' || t.action === 'EarlyClosureFeeReversal');
      const isFISA = account && account.product && account.product.productType === productTypes.FixedRateCashISA;
      
      return account.product.productType === productTypes.EasyAccess || 
             account.product.productType === productTypes.Notice ||
             (isFISA && hasFees);
  }
  

  renderActivitiesSection() {
      const groupedActivities = groupBy(this.filteredActivity(), a => a && a.bookingDate && formatDate(new Date(a.bookingDate), { weekDay: true }) || '');
  
      return (
          <table className="account-activity__table">
              {groupedActivities.length > 0 && (
              <thead>
                  <tr>
                      <th className="account-activity__table-header">Date</th>
                      <th className="account-activity__table-header">Amount</th>
                      <th className="account-activity__table-header">Balance</th>
                  </tr>
              </thead>
              )}
              <tbody>
                  {this.renderActivities()}
              </tbody>
          </table>);
  }

  renderActivities() {
    const groupedActivities = groupBy(this.filteredActivity(), a => a && a.bookingDate && formatDate(new Date(a.bookingDate), { weekDay: true }) || '');

    if (groupedActivities.length === 0) {
      return (
        <tr>
          <td colSpan={3} className="account-activity__table-cell--no-activity">
            You have no account activity.
          </td>
        </tr>
      );
    } else {
      return groupedActivities.flatMap(({ key, items }) => [
        (<tr key={key}>
          <td colSpan={3} className="account-activity__section-header">{key}</td>
        </tr>),
        ...items.flatMap(activity => this.renderActivity(activity))
      ]);
    }
  }

  renderWithdrawalDateTooltip() {
    return (
            <>
              <ul className="account-activity__blended-tooltip-items">
                <li className="account-activity__blended-tooltip-item">On the morning of your withdrawal, we&apos;ll send the money to your chosen account.</li>
              </ul>
            </>
          );
  };

  resetAllModals = () => {
    this.setState({
      cancelNoticeModalOpen: false,
      cancelNoticeSuccessModalOpen: false,
      cancelNoticeFailedModalOpen: false,
      isProcessing: false
    });
    document.body.style.overflow = 'auto'; // Enable scrolling
  };

  onCancelNoticeModalOpen = (/** @type {PendingTransaction} */ pendingTransaction) => {
      this.resetAllModals();
      this.setState({
          cancelNoticeModalOpen: true,
          pendingWithdrawalRecord: pendingTransaction
      });
      document.body.style.overflow = 'hidden'; // Disable scrolling
  };

  onCancelNoticeSuccessModalOpen = () => {
      this.resetAllModals();
      this.setState({
          cancelNoticeSuccessModalOpen: true
      });
      document.body.style.overflow = 'hidden'; // Disable scrolling
  };

  onCancelNoticeFailedModalOpen = () => {
      this.resetAllModals();
      this.setState({
          cancelNoticeFailedModalOpen: true,
          pendingWithdrawalRecord: null
      });
      document.body.style.overflow = 'hidden'; // Disable scrolling
  };

  onProcessModalOpen = () => {
      this.resetAllModals();
      this.setState({
          isProcessing: true
      });
  };

  sendCancelPendingWithdrawal = () => {
      const { account } = this.props;
      const { pendingWithdrawalRecord } = this.state;

      if (!pendingWithdrawalRecord) {
        return;
      }

      this.onProcessModalOpen();

      this.apolloClient.mutate({
          mutation: gql`
                mutation CancelNoticeWithdrawalRequest($request : CancelNoticeWithdrawalRequestType!){
                    cancelNoticeWithdrawal(request:$request) {
                      success
                    }
                } 
            `,
            variables: {
                request: {
                    accountId: account.id,
                    pendingTransactionId: pendingWithdrawalRecord.pendingTransactionId
                }
            }
        }).then(result => {
            if (result.data.cancelNoticeWithdrawal.success) {
                pendingWithdrawalRecord.state = 'cancellationrequested';
                this.onCancelNoticeSuccessModalOpen();
            } else {
                this.onCancelNoticeFailedModalOpen();
            }
        }).catch(error => {
            console.log('error::' + error);
        });
  }

  renderCancelWithdrawalModal () {
    const { pendingWithdrawalRecord } = this.state;

    if (!pendingWithdrawalRecord) {
      return;
    }

    return (
             <Modal className="modal--fixed-height modal--wide modal__mobile-optimised" onClose={this.resetAllModals}>
			   <h4 className="modal__title">Do you want to cancel this withdrawal?</h4>
               <div className="account-activity__grouping">
                 <div className="account-activity__grouping-item">
                    <p className="account-activity__slim-title">Scheduled withdrawal date</p>
                    <p className="account-activity__slim-value">{formatDate(new Date(pendingWithdrawalRecord.withdrawalDate))}</p>
                 </div>
                 <div className="account-activity__grouping-item">
                    <p className="account-activity__slim-title">Amount</p>
                    <p className="account-activity__slim-value">{pendingWithdrawalRecord.amount ? formatCurrency(Math.abs(pendingWithdrawalRecord.amount)) : '£0'}</p>
                 </div>
               </div>
               <p className="modal__slim">If you cancel this withdrawal, we won&apos;t send this money to your chosen account on the date above.</p>
               <p className="modal__slim">Don&apos;t forget that if you schedule another withdrawal in the future, you&apos;ll need to wait your full 90-day notice period again.</p>
               <div className="modal__action-group">
                 <ButtonGroup>
                   <Button size='med'
                          label='Yes, cancel my withdrawal'
                          onClick={this.sendCancelPendingWithdrawal}
                   />
                   <Button variant='secondary'
                          size='med'
                          label='No, do not cancel it'
                          onClick={this.resetAllModals}
                   />
                 </ButtonGroup>
               </div>
             </Modal>
		   );
  };

  renderCancelNoticeAccountClosureModal () {
    const { pendingWithdrawalRecord } = this.state;

    if (!pendingWithdrawalRecord) {
      return;
    }

    return (
	     	 <Modal className="modal modal--fixed-height modal--wide modal__mobile-optimised" onClose={this.resetAllModals}>
			   <h4 className="modal__title">Do you want to cancel this withdrawal and keep your account open?</h4>
               <div className="account-activity__grouping">
                 <div className="account-activity__grouping-item">
                    <p className="account-activity__slim-title">Scheduled withdrawal date</p>
                    <p className="account-activity__slim-value">{formatDate(new Date(pendingWithdrawalRecord.withdrawalDate))}</p>
                 </div>
                 <div className="account-activity__grouping-item">
                    <p className="account-activity__slim-title">Amount</p>
                    <p className="account-activity__slim-value">{pendingWithdrawalRecord.amount ? formatCurrency(Math.abs(pendingWithdrawalRecord.amount)) : '£0'}</p>
                 </div>
               </div>
               <p className="modal__slim">If you cancel this withdrawal, we won&apos;t send this money to your chosen account on the date above and this account will stay open.</p>
               <p className="modal__slim">Don&apos;t forget that if you schedule another withdrawal in the future, you&apos;ll need to wait your full 90-day notice period again.</p>
               <div className="modal__action-group">
                 <ButtonGroup>
                   <Button size='med'
                          label='Yes, cancel my withdrawal'
                          onClick={this.sendCancelPendingWithdrawal}
                   />
                   <Button variant='secondary'
                          size='med'
                          label='No, do not cancel it'
                          onClick={this.resetAllModals}
                   />
                 </ButtonGroup>
               </div>
             </Modal>
		   );
  };

  renderCancelNoticeModal() {
      const { pendingWithdrawalRecord } = this.state;

      if (pendingWithdrawalRecord && pendingWithdrawalRecord.closeAccount)
          return this.renderCancelNoticeAccountClosureModal();

      return this.renderCancelWithdrawalModal();
  }

  renderProcessingModal () {
    return (
	     	<Modal>
          <Loading message="Processing..." />
			  </Modal>
		);
  };

  renderCancelWithdrawalSuccessModal () {
      
    return (
      <Modal onClose={this.resetAllModals}>
        <img src={successImg} className='account-activity__image' />
        <h4 className="account-activity__modal-title">Done!</h4>
        <Button size='med' customClassNames={["modal__button"]} label="OK" onClick={this.resetAllModals}/>
      </Modal>
    );
  };

  renderCancelWithdrawalFailedModal() {
    return (
            <Modal onClose={this.resetAllModals}>
              <img src={errorImg} className='account-activity__image' />
              <h4 className="account-activity__title">Failed!</h4>
              <p className="account-activity__slim">Please try again later.</p>
            </Modal>
           );
  }

  toggleWithdrawalDateTooltip = (/** @type {React.MouseEvent<HTMLAnchorElement, MouseEvent>} */ event) => {
    event.preventDefault();
     
    let button = event.currentTarget;
    let rect = button.getBoundingClientRect();
    if (!button.offsetParent) {
      return;
    }

    let prect = button.offsetParent.getBoundingClientRect();

    this.setState(({ withdrawalDateToolTipOpen }) => ({
      withdrawalDateToolTipOpen: !withdrawalDateToolTipOpen,
      withdrawalDateToolTipTop: rect.top - prect.top + 230,
      withdrawalDateToolTipLeft: rect.left - prect.left - 5
    }));
  };

  renderPendingTransactionsActivitiesSection() {
    return (<table className="account-activity__table">
          <thead>
            <tr>
              <th className="account-activity__table-header">
                <div className="account-activity__label-tooltip">
                  Withdrawal date
                  <span className="account-activity__info-value">
                    <a href="#" onClick={this.toggleWithdrawalDateTooltip}><img className="" src={infoIcon} /></a>
                  </span>
                  <div ref={this.popupWithdrawalDateTooltip}
                       className={['account-activity__blended-popup', this.state.withdrawalDateToolTipOpen ? 'is-open' : ''].join(' ')}
                       style={{ top: this.state.withdrawalDateToolTipTop, left: this.state.withdrawalDateToolTipLeft }}>
                    {this.renderWithdrawalDateTooltip()}
                  </div>
                </div>
              </th>
              <th className="account-activity__table-header">Amount</th>
              <th className="account-activity__table-header">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
              {this.renderPendingTransactionsActivities()}
          </tbody>
        </table>);
  }

    renderPendingTransactionsActivities() {
        const {account} = this.props;
        /** @type {string | undefined} */
        let lastDate = '';
        return account.pendingTransactions.map(item => {
            let date = formatDate(new Date(item.withdrawalDate), {includeDay: true});
            let showHeader = date !== lastDate;
            lastDate = date;
            return (<>
                {showHeader && (<tr key={`h${item.pendingTransactionId}`}>
                    <td colSpan={3} className="account-activity__section-header">{date}</td>
                </tr>)}
                {this.renderPendingTransactionItem(item)}
            </>)
        });
    }

  /** @returns {FilterOption[]} */
  buildPendingTransactionMetadata(/** @type {PendingTransaction} */ item) {
      return [
          {label: "Withdraw to:", value: ""},
          {label: "Account", value: item.counterpartAccountType}
      ];
  }

  renderPendingTransactionMetadata(/** @type {PendingTransaction} */ item, /** @type {FilterOption[]} */ itemMetadata) {
    const { toggledActivities } = this.state;
    let metadataOpen = toggledActivities.includes(item.pendingTransactionId);
    return (
        <tr key={`${item.pendingTransactionId}-metadata`} className={['account-activity__table-row account-activity__table-row--metadata', metadataOpen ? 'is-open' : ''].join(' ')}>
        <td colSpan={3} className="account-activity__table-cell account-activity__table-cell--metadata">
          <table className="account-activity__metadata-table">
            <tbody>
              {(itemMetadata || []).map(({ label, value }) => (
                <tr key={label} className="account-activity__metadata-table-row">
                  <td className="account-activity__metadata-table-cell account-activity__metadata-table-cell--key">{label}</td>
                  <td className="account-activity__metadata-table-cell account-activity__metadata-table-cell--value">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </td>
      </tr>
    )
  }

  renderPendingTransactionItem(/** @type {PendingTransaction} */ item) {
    const { toggledActivities } = this.state;
    const { pendingWithdrawalRecord } = this.state;

    const LARGE_BREAKPOINT_WIDTH = 1024;
    const desktopView = window.innerWidth >= LARGE_BREAKPOINT_WIDTH;
    let metadataOpen = toggledActivities.includes(item.pendingTransactionId);
    const itemMetadata = this.buildPendingTransactionMetadata(item);
    const hasMetadata = itemMetadata && itemMetadata.length > 0 && itemMetadata.some(meta => meta.value && meta.value.trim() !== "");
    const isCancelRequestedState = (pendingWithdrawalRecord && pendingWithdrawalRecord.state === 'cancellationrequested'
                                        && pendingWithdrawalRecord.pendingTransactionId === item.pendingTransactionId) 
                                            || item.state === 'cancellationrequested';
    let mainRow = (
        <>
          <tr key={item.pendingTransactionId} className="account-activity__table-row1">
            <td className="account-activity__table-cell">
              {hasMetadata ? (
                <div className={['account-activity__activity-action', 'account-activity__activity-action--has-metadata', metadataOpen ? 'is-open' : ''].join(' ')}>
                  <a href='#' className="account-activity__activity-action-toggle" onClick={this.toggleActivity(item.pendingTransactionId)} >
                    <img className="account-activity__activity-action-icon" src={calendarIcon} />
                    <img className="account-activity__activity-action-chevron" src={chevDown} />
                  </a>
                  <span className="account-activity__activity-action-label">Pending withdrawal</span>
                </div>
              ) : (
                <span className={['account-activity__activity-action-label', 'account-activity__activity-action-label--no-metadata'].join(' ')}>Pending withdrawal</span>
              )}
            </td>
            <td className={['account-activity__table-cell'].join(' ')}>
              {item.amount ? `${formatCurrency(item.amount)}` : '-£0' }
            </td>
            {desktopView && <td className={['account-activity__table-cell'].join(' ')}>
              
              <div>
              {item.allowClosure && (!isCancelRequestedState && (item.state === 'requested' || item.state === 'scheduled')) && 
                    <Button size='med'
                          label='Cancel this withdrawal'
                          onClick={() => this.onCancelNoticeModalOpen(item)}
                    />
              }

              {item.allowClosure && isCancelRequestedState &&
                    <p>
                      Withdrawal cancellation requested
                    </p>
              }
              </div>
            </td>}
          </tr>
          {!desktopView && <tr key={item.pendingTransactionId} className="account-activity__table-row">
            <td className={['account-activity__table-cell'].join(' ')} colSpan={3}>
                <div>
                    {item.allowClosure && (!isCancelRequestedState && (item.state === 'requested' || item.state === 'scheduled')) &&
                        <Button variant='secondary'
                              size='med'
                              label='Cancel this withdrawal'
                              onClick={() => this.onCancelNoticeModalOpen(item)}
                        />
                    }

                    {item.allowClosure && isCancelRequestedState &&
                        <p>
                            Withdrawal cancellation requested
                    </p>
                    }
                </div>
            </td>
          </tr>}
            {item.closeAccount && <tr className={['account-activity__table-row'].join(' ')}>
                <td colSpan={3} className="account-activity__close-notice">
                    <div className="account-activity__grouping">
                        <img className="account-activity__error-icon" src={errorImg} />
                        <p>We&apos;ll also close your account for you on this date.</p>
                    </div>
                </td>
            </tr>
            }
        </>
    );
    let result = [mainRow];
    if (hasMetadata) {
      result.push(this.renderPendingTransactionMetadata(item, itemMetadata));
    }
    return result;
  }

  renderPendingTransactionsLink() {
      const { account } = this.props;
      return (<div className="account-activity__pendingTransactionsNotification">
          <a className="account-activity__pendingTransactionsNotification__link" onClick={this.selectFilters[`selectFilter_pending`]}>
              <img className="account-activity__pendingTransactionsNotification__icon" src={calendarIcon} />
              <div className="account-activity__pendingTransactionsNotification__link-text">{account.pendingTransactions.length} pending withdrawal{account.pendingTransactions.length > 1 ? 's' : ''} &gt;</div>
          </a>
      </div>);
  }

  render() {
    return (
      <div ref={this.props.activityRef} className={this.getClassName()}>
        <h2 className="account-activity__title">Account Activities</h2>
        {this.shouldShowFilter() && <div className="account-activity__filter-select-container">
          <Select
            value={this.state.filter}
            onChange={this.selectFilter}
            options={getFilterOptions(this.props.account)}
            name='filter'
            className="select account-activity__filter-select"
          />
        </div>}
        {this.shouldShowFilter() &&
          <div className="tabs">
            <ButtonGroup noGutter className="account-activity__filter-buttons">
              {getFilterOptions(this.props.account).map(({ label, value }) => (
                <div
                    key={value}
                    className={`tabs__button ${this.state.filter && value === this.state.filter.value ? 'tabs__button--selected' : ''}`}
                    onClick={this.selectFilters[`selectFilter_${value}`]}>
                      {label}
                </div>
              ))}
            </ButtonGroup>
          </div>
        }
        {this.state.filter && this.state.filter.value === "all" && this.props.account.pendingTransactions 
                && this.props.account.pendingTransactions.length > 0
                && this.renderPendingTransactionsLink()}

        {this.state.filter && this.state.filter.value !== "pending" 
                && this.renderActivitiesSection()}

        {this.state.filter && this.state.filter.value === "pending" 
                && this.renderPendingTransactionsActivitiesSection()}

        {this.state.cancelNoticeModalOpen 
                && this.renderCancelNoticeModal()}

        {this.state.cancelNoticeSuccessModalOpen 
                && this.renderCancelWithdrawalSuccessModal()}

        {this.state.cancelNoticeFailedModalOpen 
                && this.renderCancelWithdrawalFailedModal()}

        {this.state.isProcessing 
                && this.renderProcessingModal()}
            
      </div>
    );
  }
};

const AccountActivityWithApollo = (/** @type {AccountActivityProps & WithFeatureFlagsProps} */ props) => {
  const client = useApolloClient();
  return <AccountActivity {...props} client={client} />;
};

export default withFeatureFlags(AccountActivityWithApollo);
